import { useState, useEffect } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { Loader2 } from 'lucide-react';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

export function AuthenticatedImage({ src, alt, className, onClick }: AuthenticatedImageProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // Security: only fetch relative API paths with credentials. Refuse
        // absolute/external URLs so auth headers can't leak to third parties.
        if (!src.startsWith('/api/')) {
          console.warn('[AuthenticatedImage] Blocked non-/api/ image src:', src);
          if (!cancelled) setError(true);
          return;
        }

        const response = await apiRequest('GET', src);
        if (!response.ok) {
          throw new Error('Failed to load image');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        if (!cancelled) {
          setImageSrc(url);
        }
      } catch (err) {
        console.error('Failed to load image:', err);
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (imageSrc) {
        window.URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded-lg ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded-lg ${className}`}>
        <span className="text-sm text-muted-foreground">Failed to load image</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc || ''}
      alt={alt}
      className={className}
      onClick={onClick}
    />
  );
}